import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface CardProps {
  children: ReactNode
  className?: string
}

/** Biała powierzchnia z cienkim obrysem — podstawowy blok układu. */
export function Card({ children, className }: CardProps) {
  return <section className={cn('am-card', className)}>{children}</section>
}

export interface CardHeaderProps {
  /** Nagłówek Spectral, jak „Dokumenty rozpoznane przez AI". */
  title: ReactNode
  /** Prawa strona nagłówka — np. link „Skrzynka →" albo licznik. */
  action?: ReactNode
  subtitle?: ReactNode
  className?: string
}

export function CardHeader({ title, action, subtitle, className }: CardHeaderProps) {
  return (
    <header className={cn('flex items-center justify-between gap-4 border-b border-line-soft px-5 py-3.5', className)}>
      <div className="min-w-0">
        <h2 className="t-h2 truncate">{title}</h2>
        {subtitle ? <p className="t-micro mt-0.5">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0 text-micro text-muted">{action}</div> : null}
    </header>
  )
}

export function CardBody({ children, className }: CardProps) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>
}

export function CardFooter({ children, className }: CardProps) {
  return <div className={cn('border-t border-line-soft px-5 py-3 text-micro text-muted', className)}>{children}</div>
}

/** Lista wierszy w karcie (dokumenty, terminy, aktywność) — separatory bez paddingu karty. */
export function CardList({ children, className }: CardProps) {
  return <ul className={cn('divide-y divide-line-soft', className)}>{children}</ul>
}

export interface CardListItemProps {
  children: ReactNode
  onClick?: () => void
  className?: string
}

export function CardListItem({ children, onClick, className }: CardListItemProps) {
  const interactive = Boolean(onClick)
  return (
    <li
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      className={cn(
        'px-5 py-3.5',
        interactive && 'cursor-pointer transition-colors duration-fast hover:bg-rowhover focus-visible:am-focus focus:outline-none',
        className,
      )}
    >
      {children}
    </li>
  )
}
